/**
 * ProgramCheckInTab - Check-in/out functionality for program participants
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { 
  Search, CheckCircle, LogOut, Users, Clock, 
  UserCheck, Loader2, AlertCircle
} from "lucide-react";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "sonner";

const guardianFor = (registration) => registration.parent_name
  || `${registration.household?.primary_contact?.first_name || ''} ${registration.household?.primary_contact?.last_name || ''}`.trim()
  || registration.payer?.name || '—';
const attendanceTime = (value) => value && !Number.isNaN(new Date(value).getTime())
  ? format(new Date(value), 'h:mm a') : '—';

// Registration Card Component - extracted to avoid nested component issue
const RegistrationCard = ({ 
  registration, 
  isLoading, 
  onCheckIn, 
  onCheckOut 
}) => {
  const isCheckedIn = registration.check_status === 'checked_in';
  const isCheckedOut = registration.check_status === 'checked_out';

  // Support both flat fields and enriched player/household objects
  const firstName = registration.player_first_name || registration.player?.first_name || '';
  const lastName = registration.player_last_name || registration.player?.last_name || '';
  const guardianName = guardianFor(registration);

  return (
    <div className={`program-attendance-row ${
      isCheckedIn 
        ? 'bg-emerald-50 border-emerald-200' 
        : isCheckedOut
        ? 'bg-slate-50 border-slate-200'
        : 'bg-white border-slate-200 hover:border-slate-300'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold ${
            isCheckedIn 
              ? 'bg-emerald-200 text-emerald-700' 
              : 'bg-slate-200 text-slate-600'
          }`}>
            {firstName?.[0]}{lastName?.[0]}
          </div>
          <div className="program-attendance-details">
            <p className="font-semibold text-slate-900">
              {firstName} {lastName}
            </p>
            <p className="text-sm text-slate-500">
              Guardian: {guardianName}
            </p>
            <p className="program-row-meta">Division: {registration.division_name || '—'}</p>
            <p className="program-row-meta">Attendance: {isCheckedIn ? 'Checked in' : isCheckedOut ? 'Checked out' : 'Not checked in'}</p>
            {(
              <p className="text-xs text-emerald-600 mt-1">
                <Clock className="w-3 h-3 inline mr-1" />
                In: {attendanceTime(registration.checked_in_at)}
              </p>
            )}
            {(
              <p className="text-xs text-slate-500">
                Out: {attendanceTime(registration.checked_out_at)}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isCheckedIn ? (
            <Button
              onClick={() => onCheckOut(registration.registration_id)}
              disabled={isLoading}
              variant="outline"
              className="bg-white border-slate-300"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <LogOut className="w-4 h-4 mr-2" />
                  Check Out
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={() => onCheckIn(registration.registration_id)}
              disabled={isLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Check In
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const ProgramCheckInTab = ({ 
  registrations, 
  programId,
  token,
  API,
  onRefresh 
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [checkingIn, setCheckingIn] = useState(null);
  
  // Only show confirmed registrations for check-in
  const checkableRegistrations = registrations.filter(
    r => r.status === 'confirmed' || r.status === 'checked_in' || r.status === 'checked_out'
  );
  
  // Filter by search
  const filteredRegistrations = checkableRegistrations.filter(reg => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const pFirst = (reg.player_first_name || reg.player?.first_name || '').toLowerCase();
    const pLast = (reg.player_last_name || reg.player?.last_name || '').toLowerCase();
    const guardian = guardianFor(reg).toLowerCase();
    return (
      pFirst.includes(searchLower) ||
      pLast.includes(searchLower) ||
      guardian.includes(searchLower)
    );
  });

  // Group by status
  const checkedIn = filteredRegistrations.filter(r => r.check_status === 'checked_in');
  const checkedOut = filteredRegistrations.filter(r => r.check_status === 'checked_out');
  const notCheckedIn = filteredRegistrations.filter(r => !r.check_status || r.check_status === null);

  const handleCheckIn = async (registrationId) => {
    setCheckingIn(registrationId);
    try {
      await axios.post(
        `${API}/registrations/${registrationId}/checkin`,
        { action: 'check_in' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Player checked in!");
      onRefresh?.();
    } catch (error) {
      console.error("Check-in error:", error);
      toast.error("Failed to check in player");
    } finally {
      setCheckingIn(null);
    }
  };

  const handleCheckOut = async (registrationId) => {
    setCheckingIn(registrationId);
    try {
      await axios.post(
        `${API}/registrations/${registrationId}/checkin`,
        { action: 'check_out' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Player checked out!");
      onRefresh?.();
    } catch (error) {
      console.error("Check-out error:", error);
      toast.error("Failed to check out player");
    } finally {
      setCheckingIn(null);
    }
  };

  return (
    <div className="program-section program-checkin" data-program-section="checkin">
      <div className="program-section-heading"><div><h2>Check-in</h2><p>Find confirmed participants and record arrivals and departures.</p></div></div>
      {/* Stats Bar */}
      <div className="program-section-metrics">
        <Card className="program-attendance-list">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{checkedIn.length}</p>
              <p className="text-sm text-slate-500">Checked In</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="program-attendance-list">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
              <LogOut className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-600">{checkedOut.length}</p>
              <p className="text-sm text-slate-500">Checked Out</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="program-attendance-list">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{notCheckedIn.length}</p>
              <p className="text-sm text-slate-500">Not Yet</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Search by player or guardian name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Search participants for check-in"
          className="pl-12 h-10 rounded-md text-sm"
        />
      </div>

      {/* Currently Checked In */}
      {checkedIn.length > 0 && (
        <Card className="program-attendance-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-emerald-700">
              <UserCheck className="w-5 h-5" />
              Currently Checked In ({checkedIn.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {checkedIn.map(reg => (
              <RegistrationCard 
                key={reg.registration_id} 
                registration={reg}
                isLoading={checkingIn === reg.registration_id}
                onCheckIn={handleCheckIn}
                onCheckOut={handleCheckOut}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Not Checked In */}
      <Card className="program-attendance-list">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-600" />
            Ready to Check In ({notCheckedIn.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {notCheckedIn.length > 0 ? (
            notCheckedIn.map(reg => (
              <RegistrationCard 
                key={reg.registration_id} 
                registration={reg}
                isLoading={checkingIn === reg.registration_id}
                onCheckIn={handleCheckIn}
                onCheckOut={handleCheckOut}
              />
            ))
          ) : (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-200" />
              <p>Everyone is checked in!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Checked Out */}
      {checkedOut.length > 0 && (
        <Card className="program-attendance-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-slate-500">
              <LogOut className="w-5 h-5" />
              Checked Out ({checkedOut.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {checkedOut.map(reg => (
              <RegistrationCard 
                key={reg.registration_id} 
                registration={reg}
                isLoading={checkingIn === reg.registration_id}
                onCheckIn={handleCheckIn}
                onCheckOut={handleCheckOut}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* No registrations */}
      {checkableRegistrations.length === 0 && (
        <Card className="program-attendance-list">
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-slate-200" />
            <p className="text-slate-500">No confirmed registrations to check in</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProgramCheckInTab;
